import { useState } from 'react'
import { Copy, Tag, Briefcase, LineChart, EyeOff, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ExchangeBadge } from '@/components/common/ExchangeBadge'
import { useToast } from '@/hooks/use-toast'
import { addTag } from '@/lib/api'
import { extractBaseSymbol, buildCoinglassUrl } from '@/lib/utils'
import { cn } from '@/lib/utils'
import { FEATURE_SCREENER_ENABLED } from '@/lib/featureFlags'

const ALERT_TYPE_COLORS: Record<string, string> = {
    oi: 'bg-blue-500/20 text-blue-400',
    orderbook: 'bg-purple-500/20 text-purple-400',
    mark_price: 'bg-orange-500/20 text-orange-400',
    price_diff: 'bg-green-500/20 text-green-400',
}

interface AlertNotificationContentProps {
    alert: Record<string, unknown>
    onDismiss?: () => void
}

export function AlertNotificationContent({ alert, onDismiss }: AlertNotificationContentProps) {
    const { toast } = useToast()
    const [isHovered, setIsHovered] = useState(false)

    const alertType = String(alert.alert_type || 'unknown')
    const symbol = String(alert.symbol || alert.base || '')
    const exchange = String(alert.exchange || '')
    const baseSymbol = extractBaseSymbol(symbol)
    const chartUrl = buildCoinglassUrl(exchange, baseSymbol)

    const getAlertValue = () => {
        if (alertType === 'oi') {
            const pct = Number(alert.oi_change_pct || 0)
            return {
                value: `${pct > 0 ? '+' : ''}${pct.toFixed(2)}%`,
                className: pct >= 0 ? 'text-green-400' : 'text-red-400',
            }
        }
        if (alertType === 'orderbook') {
            const usd = Number(alert.usd_value || 0)
            const side = String(alert.side || '').toUpperCase()
            return {
                value: `$${(usd / 1000).toFixed(0)}K ${side}`,
                className: String(alert.side) === 'bid' ? 'text-green-400' : 'text-red-400',
            }
        }
        if (alertType === 'mark_price') {
            const pct = Number(alert.deviation_pct || alert.threshold_pct || 0)
            return {
                value: `${pct.toFixed(2)}%`,
                className: 'text-orange-400',
            }
        }
        if (alertType === 'price_diff') {
            const pct = Number(alert.max_diff_pct || 0)
            return {
                value: `${pct.toFixed(2)}%`,
                className: 'text-green-400',
            }
        }
        return { value: '-', className: '' }
    }

    const handleCopySymbol = (e: React.MouseEvent) => {
        e.stopPropagation()
        navigator.clipboard.writeText(baseSymbol || symbol)
            .then(() => toast({ title: `Copied ${baseSymbol || symbol}` }))
            .catch(() => toast({ title: 'Failed to copy', variant: 'destructive' }))
    }

    const handleAddSpamTag = async (e: React.MouseEvent) => {
        e.stopPropagation()
        if (baseSymbol && exchange) {
            const tagName = `${exchange.toLowerCase()}_spam`
            try {
                await addTag(baseSymbol, tagName)
                toast({ title: `Added ${tagName.toUpperCase()} tag to ${baseSymbol}` })
            } catch {
                toast({ title: 'Failed to add tag', variant: 'destructive' })
            }
        }
    }

    const handleOpenPositions = (e: React.MouseEvent) => {
        e.stopPropagation()
        window.open(`/positions?symbol=${baseSymbol}&exchange=${exchange}`, '_blank')
        onDismiss?.()
    }

    const handleOpenScreener = (e: React.MouseEvent) => {
        e.stopPropagation()
        window.open(`/screener?ticker=${baseSymbol}`, '_blank')
        onDismiss?.()
    }

    const handleDismiss = (e: React.MouseEvent) => {
        e.stopPropagation()
        onDismiss?.()
    }

    const handleOpenChart = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        window.open(chartUrl, '_blank')
    }

    const { value, className: valueClass } = getAlertValue()

    const getAlertTypeLabel = () => {
        switch (alertType) {
            case 'mark_price': return 'MP'
            case 'price_diff': return 'PD'
            case 'orderbook': return 'OB'
            default: return alertType.toUpperCase()
        }
    }

    return (
        <div
            className="flex flex-col gap-1 w-full min-w-0"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Compact single-line view */}
            <div className="flex items-center gap-2">
                <Badge className={cn('text-[10px] px-1.5 py-0 shrink-0', ALERT_TYPE_COLORS[alertType] || '')}>
                    {getAlertTypeLabel()}
                </Badge>
                <ExchangeBadge exchange={exchange} size="sm" />
                <button
                    type="button"
                    onClick={handleCopySymbol}
                    className="font-medium text-sm hover:text-primary truncate"
                >
                    {baseSymbol || symbol}
                </button>
                <span className={cn('font-semibold text-sm ml-auto', valueClass)}>
                    {value}
                </span>
                <a
                    href={chartUrl}
                    onClick={handleOpenChart}
                    className="text-muted-foreground hover:text-primary shrink-0"
                >
                    <ExternalLink className="h-3 w-3" />
                </a>
            </div>

            {/* Action buttons - shown on hover */}
            {isHovered && (
                <div className="flex items-center gap-0.5 pt-1 border-t border-border/50 mt-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-[10px]"
                        onClick={handleCopySymbol}
                    >
                        <Copy className="h-3 w-3" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-[10px]"
                        onClick={handleAddSpamTag}
                    >
                        <Tag className="h-3 w-3" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-[10px]"
                        onClick={handleOpenPositions}
                    >
                        <Briefcase className="h-3 w-3" />
                    </Button>
                    {FEATURE_SCREENER_ENABLED && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-[10px]"
                        onClick={handleOpenScreener}
                    >
                        <LineChart className="h-3 w-3" />
                    </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-[10px] hover:text-red-500 ml-auto"
                        onClick={handleDismiss}
                    >
                        <EyeOff className="h-3 w-3" />
                    </Button>
                </div>
            )}
        </div>
    )
}
